import { useEffect, useMemo, useRef } from 'react';
import * as vp from 'vopay';
import * as Yup from 'yup';

import { createCheckBoxField, createFileUpload, createSelectField, createNumberFieldLead } from './useFields.helpers';

import fieldUtilsFactory from './fieldUtils.factory';
import type { FieldUtils, SubSection } from './types';
import type { MultiStepFormCtx } from '../../providers/MultiStepForm.provider';

import { NewAccountData } from '../../components/Accounts/types';
import { usePartnerLogin } from '../../providers/PartnerLogin.provider';
import { useHubSpotDropdownDataSet } from '../../providers/HubSpotDropdownData.provider';
import { useAccountTransactionTypes } from '../../providers/AccountTransactionTypes.provider';
import { AccountTransactionType } from 'vopay';

export type AccountMatrixSections = {
  flowOfFunds: SubSection<NewAccountData>[];
  matrix: SubSection<NewAccountData>[];
};

export type AccountMatrixSection = keyof AccountMatrixSections;
export type Ctx = MultiStepFormCtx<NewAccountData>;

const useAccountMatrixCreationFields = (): FieldUtils<NewAccountData> => {
  const { response } = usePartnerLogin();
  const { isInsideSales } = response;
  const { transactionTypes } = useAccountTransactionTypes();
  const { dealCountry } = useHubSpotDropdownDataSet();
  const accountTransactionTypes = useRef<AccountTransactionType[]>([]);
  const country = useRef<string>('');

  const getFlowOfFundsFileName = () => 'Flow of Funds Diagram will be uploaded.';

  const onlySendingMoney = 'Only Sending Money';
  const onlyCollectingMoney = 'Only Collecting Money';
  const sendingAndCollectingMoney = 'Sending and Collecting Money';

  const hubspotCountryUS = 'US';
  const hubspotCountryCA = 'CA';

  const countryUS = '2';
  const countryCA = '1';

  const transactionTypeEFTFunding = 'EFT Funding';
  const transactionTypeACHFunding = 'ACH Funding';
  const transactionTypeInteracMoneyRequest = 'Interac Money Request';
  const transactionTypeCreditCard = 'Credit Card';
  const transactionTypeFedwireFunding = 'Fedwire Funding';
  const transactionTypeEFTWithdrawal = 'EFT Withdrawal';
  const transactionTypeACHWithdrawal = 'ACH Withdrawal';
  const transactionTypeInteracBulkPayout = 'Interac Bulk Payout';
  const transactionTypeVoPayInstantWithdraw = 'VoPayInstant Withdraw';
  const transactionTypeFedwireWithdrawal = 'Fedwire Withdrawal';
  const transactionTypeRTPWithdrawal = 'RTP Withdrawal';
  const transactionTypeOCT = 'Push To Card';

  useEffect(() => {
    if (dealCountry) {
      switch (dealCountry) {
        case hubspotCountryUS:
          country.current = countryUS;
          break;
        case hubspotCountryCA:
          country.current = countryCA;
          break;
        default:
          country.current = '';
          break;
      }
    }
  }, [dealCountry]);

  useEffect(() => {
    accountTransactionTypes.current = transactionTypes;
  }, [transactionTypes]);

  const displayMatrixFields = (transactionType: string) => {
    const transactionTypes = Object.values(accountTransactionTypes.current)
      .filter((accountTransactionType) => accountTransactionType.TransactionType === transactionType)
      .map((accountTransactionType) => {
        if (!accountTransactionType.ADRCountries_ID) {
          return true;
        }
        return accountTransactionType.ADRCountries_ID === country.current;
      });
    if (transactionTypes.length > 0) {
      return transactionTypes.pop() || false;
    }
    return false;
  };

  const sections: AccountMatrixSections = {
    flowOfFunds: [
      {
        label: 'Flow of Funds',
        fields: {
          flowOfFundsDiagram: {
            label: 'Flow of Funds Diagram',
            placeholder: 'Flow of Funds Diagram',
            fieldName: 'flowOfFundsDiagram',
            initValue: '',
            dataType: 'file',
            accept: 'jpg,jpeg,pdf',
            fileName: () => getFlowOfFundsFileName(),
            showField: () => isInsideSales,
            getInputState: () => (isInsideSales ? 'required' : 'optional'),
            getElement(ctx: Ctx) {
              return createFileUpload(this, ctx, 10);
            },
            validate() {
              if (isInsideSales) {
                return Yup.string().required();
              }
              return Yup.string().notRequired();
            },
          },
          accountIntendedTransactionType: {
            label: 'Select the type of transactions that the account is planning to do with VoPay',
            placeholder: 'Select the type of transaction',
            fieldName: 'accountIntendedTransactionType',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => (isInsideSales ? 'required' : 'optional'),
            showField: () => isInsideSales,
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx, { noOrdering: true });
            },
            getSelectOptions: () => vp.ACCOUNT_INTENDED_TRANSACTION_TYPES,
            validate() {
              if (isInsideSales) {
                return Yup.string().required('Please select the type of transaction.');
              }
              return Yup.string().notRequired();
            },
          },
        },
        showSection: () => isInsideSales,
      },
    ],
    matrix: [
      {
        label: 'How is the company funding their VoPay Account?',
        fields: {
          NTPFundingEFTEnabled: {
            label: 'EFT',
            fieldName: 'NTPFundingEFTEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeEFTFunding) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPFundingEFTAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPFundingEFTEnabled ?? false,
                  ctx.values.NTPFundingInteracEnabled ?? false,
                  ctx.values.NTPFundingCreditCardEnabled ?? false,
                  ctx.values.NTPFundingACHEnabled ?? false,
                  ctx.values.NTPFundingACHSameDayEnabled ?? false,
                  ctx.values.NTPFundingFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPFundingEFTAmount: {
            label: 'Total amount funded monthly with EFT',
            placeholder: 'Total amount funded monthly',
            fieldName: 'NTPFundingEFTAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.NTPFundingEFTEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPFundingEFTEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPFundingACHEnabled: {
            label: 'ACH',
            fieldName: 'NTPFundingACHEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeACHFunding) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPFundingACHAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPFundingEFTEnabled ?? false,
                  ctx.values.NTPFundingInteracEnabled ?? false,
                  ctx.values.NTPFundingCreditCardEnabled ?? false,
                  ctx.values.NTPFundingACHEnabled ?? false,
                  ctx.values.NTPFundingACHSameDayEnabled ?? false,
                  ctx.values.NTPFundingFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPFundingACHAmount: {
            label: 'Total amount funded monthly with ACH',
            placeholder: 'Total amount funded monthly',
            fieldName: 'NTPFundingACHAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.NTPFundingACHEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPFundingACHEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPFundingACHSameDayEnabled: {
            label: 'Same Day ACH',
            fieldName: 'NTPFundingACHSameDayEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeACHFunding) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPFundingACHSameDayAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPFundingEFTEnabled ?? false,
                  ctx.values.NTPFundingInteracEnabled ?? false,
                  ctx.values.NTPFundingCreditCardEnabled ?? false,
                  ctx.values.NTPFundingACHEnabled ?? false,
                  ctx.values.NTPFundingACHSameDayEnabled ?? false,
                  ctx.values.NTPFundingFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPFundingACHSameDayAmount: {
            label: 'Total amount funded monthly with same day ACH',
            placeholder: 'Total amount funded monthly',
            fieldName: 'NTPFundingACHSameDayAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.NTPFundingACHSameDayEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPFundingACHSameDayEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPFundingInteracEnabled: {
            label: 'Interac e-Transfer Money Request',
            fieldName: 'NTPFundingInteracEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeInteracMoneyRequest) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPFundingInteracAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPFundingEFTEnabled ?? false,
                  ctx.values.NTPFundingInteracEnabled ?? false,
                  ctx.values.NTPFundingCreditCardEnabled ?? false,
                  ctx.values.NTPFundingACHEnabled ?? false,
                  ctx.values.NTPFundingACHSameDayEnabled ?? false,
                  ctx.values.NTPFundingFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPFundingInteracAmount: {
            label: 'Total amount funded monthly with Interac e-Transfer Money Request',
            placeholder: 'Total amount funded monthly',
            fieldName: 'NTPFundingInteracAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.NTPFundingInteracEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPFundingInteracEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPFundingCreditCardEnabled: {
            label: 'Credit Card',
            fieldName: 'NTPFundingCreditCardEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeCreditCard) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPFundingCreditCardAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPFundingEFTEnabled ?? false,
                  ctx.values.NTPFundingInteracEnabled ?? false,
                  ctx.values.NTPFundingCreditCardEnabled ?? false,
                  ctx.values.NTPFundingACHEnabled ?? false,
                  ctx.values.NTPFundingACHSameDayEnabled ?? false,
                  ctx.values.NTPFundingFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPFundingCreditCardAmount: {
            label: 'Total amount funded monthly with Credit Card',
            placeholder: 'Total amount funded monthly',
            fieldName: 'NTPFundingCreditCardAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.NTPFundingCreditCardEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPFundingCreditCardEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPFundingFedwireEnabled: {
            label: 'Fedwire',
            fieldName: 'NTPFundingFedwireEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeFedwireFunding) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPFundingFedwireAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPFundingEFTEnabled ?? false,
                  ctx.values.NTPFundingInteracEnabled ?? false,
                  ctx.values.NTPFundingCreditCardEnabled ?? false,
                  ctx.values.NTPFundingACHEnabled ?? false,
                  ctx.values.NTPFundingACHSameDayEnabled ?? false,
                  ctx.values.NTPFundingFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPFundingFedwireAmount: {
            label: 'Total amount funded monthly with Fedwire',
            placeholder: 'Total amount funded monthly',
            fieldName: 'NTPFundingFedwireAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.NTPFundingFedwireEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPFundingFedwireEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPFundingTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPFundingEFTAmount ?? '',
                        ctx.values.NTPFundingInteracAmount ?? '',
                        ctx.values.NTPFundingACHAmount ?? '',
                        ctx.values.NTPFundingACHSameDayAmount ?? '',
                        ctx.values.NTPFundingCreditCardAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPFundingTotalAmount: {
            label: 'Total',
            placeholder: 'Total amount',
            fieldName: 'NTPFundingTotalAmount',
            initValue: '',
            dataType: 'number',
            getInputState: () => 'disabled',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPFundingAvgFundedAmount: {
            label: 'The average funded amount is',
            placeholder: 'Average amount funded monthly',
            fieldName: 'NTPFundingAvgFundedAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPFundingLargestFundedAmount: {
            label: 'The largest funded amount is',
            placeholder: 'The largest funded amount',
            fieldName: 'NTPFundingLargestFundedAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
        },
        showSection: (ctx: Ctx) =>
          isInsideSales &&
          [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
      },
      {
        label:
          'How does the company intend to withdraw the collected amount from their VoPay Account to their Bank Account?',
        fields: {
          NTPWithdrawEFTEnabled: {
            label: 'EFT',
            fieldName: 'NTPWithdrawEFTEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeEFTWithdrawal) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPWithdrawEFTAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPWithdrawEFTEnabled ?? false,
                  ctx.values.NTPWithdrawInteracEnabled ?? false,
                  ctx.values.NTPWithdrawVPInstantEnabled ?? false,
                  ctx.values.NTPWithdrawP2CEnabled ?? false,
                  ctx.values.NTPWithdrawACHEnabled ?? false,
                  ctx.values.NTPWithdrawACHSameDayEnabled ?? false,
                  ctx.values.NTPWithdrawFedwireEnabled ?? false,
                  ctx.values.NTPWithdrawRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPWithdrawEFTAmount: {
            label: 'Total amount withdrawn monthly with EFT',
            placeholder: 'Total amount withdrawn monthly',
            fieldName: 'NTPWithdrawEFTAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.NTPWithdrawEFTEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPWithdrawEFTEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawACHEnabled: {
            label: 'ACH',
            fieldName: 'NTPWithdrawACHEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeACHWithdrawal) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPWithdrawACHAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPWithdrawEFTEnabled ?? false,
                  ctx.values.NTPWithdrawInteracEnabled ?? false,
                  ctx.values.NTPWithdrawVPInstantEnabled ?? false,
                  ctx.values.NTPWithdrawP2CEnabled ?? false,
                  ctx.values.NTPWithdrawACHEnabled ?? false,
                  ctx.values.NTPWithdrawACHSameDayEnabled ?? false,
                  ctx.values.NTPWithdrawFedwireEnabled ?? false,
                  ctx.values.NTPWithdrawRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPWithdrawACHAmount: {
            label: 'Total amount withdrawn monthly with ACH',
            placeholder: 'Total amount withdrawn monthly',
            fieldName: 'NTPWithdrawACHAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.NTPWithdrawACHEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPWithdrawACHEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawACHSameDayEnabled: {
            label: 'Same Day ACH',
            fieldName: 'NTPWithdrawACHSameDayEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeACHWithdrawal) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPWithdrawACHSameDayAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPWithdrawEFTEnabled ?? false,
                  ctx.values.NTPWithdrawInteracEnabled ?? false,
                  ctx.values.NTPWithdrawVPInstantEnabled ?? false,
                  ctx.values.NTPWithdrawP2CEnabled ?? false,
                  ctx.values.NTPWithdrawACHEnabled ?? false,
                  ctx.values.NTPWithdrawACHSameDayEnabled ?? false,
                  ctx.values.NTPWithdrawFedwireEnabled ?? false,
                  ctx.values.NTPWithdrawRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPWithdrawACHSameDayAmount: {
            label: 'Total amount withdrawn monthly with same day ACH',
            placeholder: 'Total amount withdrawn monthly',
            fieldName: 'NTPWithdrawACHSameDayAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.NTPWithdrawACHSameDayEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPWithdrawACHSameDayEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawInteracEnabled: {
            label: 'Interac e-Transfer',
            fieldName: 'NTPWithdrawInteracEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeInteracBulkPayout) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPWithdrawInteracAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPWithdrawEFTEnabled ?? false,
                  ctx.values.NTPWithdrawInteracEnabled ?? false,
                  ctx.values.NTPWithdrawVPInstantEnabled ?? false,
                  ctx.values.NTPWithdrawP2CEnabled ?? false,
                  ctx.values.NTPWithdrawACHEnabled ?? false,
                  ctx.values.NTPWithdrawACHSameDayEnabled ?? false,
                  ctx.values.NTPWithdrawFedwireEnabled ?? false,
                  ctx.values.NTPWithdrawRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPWithdrawInteracAmount: {
            label: 'Total amount withdrawn monthly with Interac',
            placeholder: 'Total amount withdrawn monthly',
            fieldName: 'NTPWithdrawInteracAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.NTPWithdrawInteracEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPWithdrawInteracEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawVPInstantEnabled: {
            label: 'VoPay Instant',
            fieldName: 'NTPWithdrawVPInstantEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeVoPayInstantWithdraw) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPWithdrawVPInstantAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPWithdrawEFTEnabled ?? false,
                  ctx.values.NTPWithdrawInteracEnabled ?? false,
                  ctx.values.NTPWithdrawVPInstantEnabled ?? false,
                  ctx.values.NTPWithdrawP2CEnabled ?? false,
                  ctx.values.NTPWithdrawACHEnabled ?? false,
                  ctx.values.NTPWithdrawACHSameDayEnabled ?? false,
                  ctx.values.NTPWithdrawFedwireEnabled ?? false,
                  ctx.values.NTPWithdrawRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPWithdrawVPInstantAmount: {
            label: 'Total amount withdrawn monthly with VoPay Instant',
            placeholder: 'Total amount withdrawn monthly',
            fieldName: 'NTPWithdrawVPInstantAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.NTPWithdrawVPInstantEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPWithdrawVPInstantEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawFedwireEnabled: {
            label: 'Fedwire',
            fieldName: 'NTPWithdrawFedwireEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeFedwireWithdrawal) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPWithdrawFedwireAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPWithdrawEFTEnabled ?? false,
                  ctx.values.NTPWithdrawInteracEnabled ?? false,
                  ctx.values.NTPWithdrawVPInstantEnabled ?? false,
                  ctx.values.NTPWithdrawP2CEnabled ?? false,
                  ctx.values.NTPWithdrawACHEnabled ?? false,
                  ctx.values.NTPWithdrawACHSameDayEnabled ?? false,
                  ctx.values.NTPWithdrawFedwireEnabled ?? false,
                  ctx.values.NTPWithdrawRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPWithdrawFedwireAmount: {
            label: 'Total amount withdrawn monthly with Fedwire',
            placeholder: 'Total amount withdrawn monthly',
            fieldName: 'NTPWithdrawFedwireAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.NTPWithdrawFedwireEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPWithdrawFedwireEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawRTPEnabled: {
            label: 'RTP',
            fieldName: 'NTPWithdrawRTPEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeRTPWithdrawal) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPWithdrawRTPAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPWithdrawEFTEnabled ?? false,
                  ctx.values.NTPWithdrawInteracEnabled ?? false,
                  ctx.values.NTPWithdrawVPInstantEnabled ?? false,
                  ctx.values.NTPWithdrawP2CEnabled ?? false,
                  ctx.values.NTPWithdrawACHEnabled ?? false,
                  ctx.values.NTPWithdrawACHSameDayEnabled ?? false,
                  ctx.values.NTPWithdrawFedwireEnabled ?? false,
                  ctx.values.NTPWithdrawRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPWithdrawRTPAmount: {
            label: 'Total amount withdrawn monthly with RTP',
            placeholder: 'Total amount withdrawn monthly',
            fieldName: 'NTPWithdrawRTPAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.NTPWithdrawRTPEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPWithdrawRTPEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawP2CAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawP2CEnabled: {
            label: 'OCT',
            fieldName: 'NTPWithdrawP2CEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeOCT) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('NTPWithdrawP2CAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.NTPWithdrawEFTEnabled ?? false,
                  ctx.values.NTPWithdrawInteracEnabled ?? false,
                  ctx.values.NTPWithdrawVPInstantEnabled ?? false,
                  ctx.values.NTPWithdrawP2CEnabled ?? false,
                  ctx.values.NTPWithdrawACHEnabled ?? false,
                  ctx.values.NTPWithdrawACHSameDayEnabled ?? false,
                  ctx.values.NTPWithdrawFedwireEnabled ?? false,
                  ctx.values.NTPWithdrawRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          NTPWithdrawP2CAmount: {
            label: 'Total amount withdrawn monthly with Push to Card',
            placeholder: 'Total amount withdrawn monthly',
            fieldName: 'NTPWithdrawP2CAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.NTPWithdrawP2CEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.NTPWithdrawP2CEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'NTPWithdrawTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.NTPWithdrawEFTAmount ?? '',
                        ctx.values.NTPWithdrawInteracAmount ?? '',
                        ctx.values.NTPWithdrawVPInstantAmount ?? '',
                        ctx.values.NTPWithdrawACHAmount ?? '',
                        ctx.values.NTPWithdrawACHSameDayAmount ?? '',
                        ctx.values.NTPWithdrawFedwireAmount ?? '',
                        ctx.values.NTPWithdrawRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawTotalAmount: {
            label: 'Total',
            placeholder: 'Total amount',
            fieldName: 'NTPWithdrawTotalAmount',
            initValue: '',
            dataType: 'number',
            getInputState: () => 'disabled',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawAvgWithdrawAmount: {
            label: 'The average withdrawn amount is',
            placeholder: 'Average amount withdrawn monthly',
            fieldName: 'NTPWithdrawAvgWithdrawAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          NTPWithdrawLargestWithdrawAmount: {
            label: 'The largest withdrawn amount is',
            placeholder: 'The largest withdrawn amount',
            fieldName: 'NTPWithdrawLargestWithdrawAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
        },
        showSection: (ctx: Ctx) =>
          isInsideSales &&
          [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
      },
      {
        label: 'How much funds does the company intend to send from their VoPay account to third parties?',
        fields: {
          TPFundRequestEFTEnabled: {
            label: 'EFT',
            fieldName: 'TPFundRequestEFTEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeEFTWithdrawal) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPFundRequestEFTAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPFundRequestEFTEnabled ?? false,
                  ctx.values.TPFundRequestInteracEnabled ?? false,
                  ctx.values.TPFundRequestVPInstantEnabled ?? false,
                  ctx.values.TPFundRequestP2CEnabled ?? false,
                  ctx.values.TPFundRequestACHEnabled ?? false,
                  ctx.values.TPFundRequestACHSameDayEnabled ?? false,
                  ctx.values.TPFundRequestFedwireEnabled ?? false,
                  ctx.values.TPFundRequestRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPFundRequestEFTAmount: {
            label: 'Total amount sent monthly with EFT',
            placeholder: 'Total amount sent monthly',
            fieldName: 'TPFundRequestEFTAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.TPFundRequestEFTEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPFundRequestEFTEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestACHEnabled: {
            label: 'ACH',
            fieldName: 'TPFundRequestACHEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeACHWithdrawal) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPFundRequestACHAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPFundRequestEFTEnabled ?? false,
                  ctx.values.TPFundRequestInteracEnabled ?? false,
                  ctx.values.TPFundRequestVPInstantEnabled ?? false,
                  ctx.values.TPFundRequestP2CEnabled ?? false,
                  ctx.values.TPFundRequestACHEnabled ?? false,
                  ctx.values.TPFundRequestACHSameDayEnabled ?? false,
                  ctx.values.TPFundRequestFedwireEnabled ?? false,
                  ctx.values.TPFundRequestRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPFundRequestACHAmount: {
            label: 'Total amount sent monthly with ACH',
            placeholder: 'Total amount sent monthly',
            fieldName: 'TPFundRequestACHAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.TPFundRequestACHEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPFundRequestACHEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestACHSameDayEnabled: {
            label: 'Same Day ACH',
            fieldName: 'TPFundRequestACHSameDayEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeACHWithdrawal) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPFundRequestACHSameDayAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPFundRequestEFTEnabled ?? false,
                  ctx.values.TPFundRequestInteracEnabled ?? false,
                  ctx.values.TPFundRequestVPInstantEnabled ?? false,
                  ctx.values.TPFundRequestP2CEnabled ?? false,
                  ctx.values.TPFundRequestACHEnabled ?? false,
                  ctx.values.TPFundRequestACHSameDayEnabled ?? false,
                  ctx.values.TPFundRequestFedwireEnabled ?? false,
                  ctx.values.TPFundRequestRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPFundRequestACHSameDayAmount: {
            label: 'Total amount sent monthly with same day ACH',
            placeholder: 'Total amount sent monthly',
            fieldName: 'TPFundRequestACHSameDayAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.TPFundRequestACHSameDayEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPFundRequestACHSameDayEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestInteracEnabled: {
            label: 'Interac',
            fieldName: 'TPFundRequestInteracEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeInteracBulkPayout) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPFundRequestInteracAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPFundRequestEFTEnabled ?? false,
                  ctx.values.TPFundRequestInteracEnabled ?? false,
                  ctx.values.TPFundRequestVPInstantEnabled ?? false,
                  ctx.values.TPFundRequestP2CEnabled ?? false,
                  ctx.values.TPFundRequestACHEnabled ?? false,
                  ctx.values.TPFundRequestACHSameDayEnabled ?? false,
                  ctx.values.TPFundRequestFedwireEnabled ?? false,
                  ctx.values.TPFundRequestRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPFundRequestInteracAmount: {
            label: 'Total amount sent monthly with Interac',
            placeholder: 'Total amount sent monthly',
            fieldName: 'TPFundRequestInteracAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.TPFundRequestInteracEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPFundRequestInteracEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestVPInstantEnabled: {
            label: 'VoPay Instant',
            fieldName: 'TPFundRequestVPInstantEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeVoPayInstantWithdraw) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPFundRequestVPInstantAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPFundRequestEFTEnabled ?? false,
                  ctx.values.TPFundRequestInteracEnabled ?? false,
                  ctx.values.TPFundRequestVPInstantEnabled ?? false,
                  ctx.values.TPFundRequestP2CEnabled ?? false,
                  ctx.values.TPFundRequestACHEnabled ?? false,
                  ctx.values.TPFundRequestACHSameDayEnabled ?? false,
                  ctx.values.TPFundRequestFedwireEnabled ?? false,
                  ctx.values.TPFundRequestRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPFundRequestVPInstantAmount: {
            label: 'Total amount sent monthly with VoPay Instant',
            placeholder: 'Total amount sent monthly',
            fieldName: 'TPFundRequestVPInstantAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.TPFundRequestVPInstantEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPFundRequestVPInstantEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestP2CEnabled: {
            label: 'OCT',
            fieldName: 'TPFundRequestP2CEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeOCT) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPFundRequestP2CAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPFundRequestEFTEnabled ?? false,
                  ctx.values.TPFundRequestInteracEnabled ?? false,
                  ctx.values.TPFundRequestVPInstantEnabled ?? false,
                  ctx.values.TPFundRequestP2CEnabled ?? false,
                  ctx.values.TPFundRequestACHEnabled ?? false,
                  ctx.values.TPFundRequestACHSameDayEnabled ?? false,
                  ctx.values.TPFundRequestFedwireEnabled ?? false,
                  ctx.values.TPFundRequestRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPFundRequestP2CAmount: {
            label: 'Total amount sent monthly with OCT',
            placeholder: 'Total amount sent monthly',
            fieldName: 'TPFundRequestP2CAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.TPFundRequestP2CEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPFundRequestP2CEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestFedwireEnabled: {
            label: 'Fedwire',
            fieldName: 'TPFundRequestFedwireEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeFedwireWithdrawal) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPFundRequestFedwireAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPFundRequestEFTEnabled ?? false,
                  ctx.values.TPFundRequestInteracEnabled ?? false,
                  ctx.values.TPFundRequestVPInstantEnabled ?? false,
                  ctx.values.TPFundRequestP2CEnabled ?? false,
                  ctx.values.TPFundRequestACHEnabled ?? false,
                  ctx.values.TPFundRequestACHSameDayEnabled ?? false,
                  ctx.values.TPFundRequestFedwireEnabled ?? false,
                  ctx.values.TPFundRequestRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPFundRequestFedwireAmount: {
            label: 'Total amount sent monthly with Fedwire',
            placeholder: 'Total amount sent monthly',
            fieldName: 'TPFundRequestFedwireAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.TPFundRequestFedwireEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPFundRequestFedwireEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestRTPAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestRTPEnabled: {
            label: 'RTP',
            fieldName: 'TPFundRequestRTPEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeRTPWithdrawal) &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPFundRequestRTPAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPFundRequestEFTEnabled ?? false,
                  ctx.values.TPFundRequestInteracEnabled ?? false,
                  ctx.values.TPFundRequestVPInstantEnabled ?? false,
                  ctx.values.TPFundRequestP2CEnabled ?? false,
                  ctx.values.TPFundRequestACHEnabled ?? false,
                  ctx.values.TPFundRequestACHSameDayEnabled ?? false,
                  ctx.values.TPFundRequestFedwireEnabled ?? false,
                  ctx.values.TPFundRequestRTPEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPFundRequestRTPAmount: {
            label: 'Total amount sent monthly with RTP',
            placeholder: 'Total amount sent monthly',
            fieldName: 'TPFundRequestRTPAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '') &&
              ctx.values.TPFundRequestRTPEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlySendingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPFundRequestRTPEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPFundRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPFundRequestInteracAmount ?? '',
                        ctx.values.TPFundRequestVPInstantAmount ?? '',
                        ctx.values.TPFundRequestP2CAmount ?? '',
                        ctx.values.TPFundRequestEFTAmount ?? '',
                        ctx.values.TPFundRequestACHAmount ?? '',
                        ctx.values.TPFundRequestACHSameDayAmount ?? '',
                        ctx.values.TPFundRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestTotalAmount: {
            label: 'Total',
            placeholder: 'Total amount',
            fieldName: 'TPFundRequestTotalAmount',
            initValue: '',
            dataType: 'number',
            getInputState: () => 'disabled',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestAvgRequestAmount: {
            label: 'The average sent amount is',
            placeholder: 'Average amount sent monthly',
            fieldName: 'TPFundRequestAvgRequestAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPFundRequestLargestRequestAmount: {
            label: 'The largest sent amount is',
            placeholder: 'The largest sent amount',
            fieldName: 'TPFundRequestLargestRequestAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
        },
        showSection: (ctx: Ctx) =>
          isInsideSales &&
          [onlySendingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
      },
      {
        label: 'How much funds does the company intend to collect from their third parties?',
        fields: {
          TPCollectRequestEFTEnabled: {
            label: 'EFT',
            fieldName: 'TPCollectRequestEFTEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeEFTFunding) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPCollectRequestEFTAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPCollectRequestEFTEnabled ?? false,
                  ctx.values.TPCollectRequestInteracEnabled ?? false,
                  ctx.values.TPCollectRequestCreditCardEnabled ?? false,
                  ctx.values.TPCollectRequestACHEnabled ?? false,
                  ctx.values.TPCollectRequestACHSameDayEnabled ?? false,
                  ctx.values.TPCollectRequestFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPCollectRequestEFTAmount: {
            label: 'Total amount collected monthly with EFT',
            placeholder: 'Total amount collected monthly',
            fieldName: 'TPCollectRequestEFTAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.TPCollectRequestEFTEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPCollectRequestEFTEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPCollectRequestACHEnabled: {
            label: 'ACH',
            fieldName: 'TPCollectRequestACHEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeACHFunding) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPCollectRequestACHAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPCollectRequestEFTEnabled ?? false,
                  ctx.values.TPCollectRequestInteracEnabled ?? false,
                  ctx.values.TPCollectRequestCreditCardEnabled ?? false,
                  ctx.values.TPCollectRequestACHEnabled ?? false,
                  ctx.values.TPCollectRequestACHSameDayEnabled ?? false,
                  ctx.values.TPCollectRequestFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPCollectRequestACHAmount: {
            label: 'Total amount collected monthly with ACH',
            placeholder: 'Total amount collected monthly',
            fieldName: 'TPCollectRequestACHAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.TPCollectRequestACHEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPCollectRequestACHEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPCollectRequestACHSameDayEnabled: {
            label: 'Same Day ACH',
            fieldName: 'TPCollectRequestACHSameDayEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeACHFunding) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPCollectRequestACHSameDayAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPCollectRequestEFTEnabled ?? false,
                  ctx.values.TPCollectRequestInteracEnabled ?? false,
                  ctx.values.TPCollectRequestCreditCardEnabled ?? false,
                  ctx.values.TPCollectRequestACHEnabled ?? false,
                  ctx.values.TPCollectRequestACHSameDayEnabled ?? false,
                  ctx.values.TPCollectRequestFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPCollectRequestACHSameDayAmount: {
            label: 'Total amount collected monthly with same day ACH',
            placeholder: 'Total amount collected monthly',
            fieldName: 'TPCollectRequestACHSameDayAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.TPCollectRequestACHSameDayEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPCollectRequestACHSameDayEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPCollectRequestInteracEnabled: {
            label: 'Interac',
            fieldName: 'TPCollectRequestInteracEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeInteracMoneyRequest) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPCollectRequestInteracAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPCollectRequestEFTEnabled ?? false,
                  ctx.values.TPCollectRequestInteracEnabled ?? false,
                  ctx.values.TPCollectRequestCreditCardEnabled ?? false,
                  ctx.values.TPCollectRequestACHEnabled ?? false,
                  ctx.values.TPCollectRequestACHSameDayEnabled ?? false,
                  ctx.values.TPCollectRequestFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPCollectRequestInteracAmount: {
            label: 'Total amount collected monthly with Interac',
            placeholder: 'Total amount collected monthly',
            fieldName: 'TPCollectRequestInteracAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.TPCollectRequestInteracEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPCollectRequestInteracEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPCollectRequestCreditCardEnabled: {
            label: 'Credit Card',
            fieldName: 'TPCollectRequestCreditCardEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeCreditCard) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPCollectRequestCreditCardAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPCollectRequestEFTEnabled ?? false,
                  ctx.values.TPCollectRequestInteracEnabled ?? false,
                  ctx.values.TPCollectRequestCreditCardEnabled ?? false,
                  ctx.values.TPCollectRequestACHEnabled ?? false,
                  ctx.values.TPCollectRequestACHSameDayEnabled ?? false,
                  ctx.values.TPCollectRequestFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPCollectRequestCreditCardAmount: {
            label: 'Total amount collected monthly with Credit Card',
            placeholder: 'Total amount collected monthly',
            fieldName: 'TPCollectRequestCreditCardAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.TPCollectRequestCreditCardEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPCollectRequestCreditCardEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                        ctx.values.TPCollectRequestFedwireAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPCollectRequestFedwireEnabled: {
            label: 'Fedwire',
            fieldName: 'TPCollectRequestFedwireEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              displayMatrixFields(transactionTypeFedwireFunding) &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createCheckBoxField(this, ctx, undefined, undefined, (event) => {
                if (event && event.target && !event.target.checked) {
                  ctx.handleFieldChange(vp.createSyntheticEvent('TPCollectRequestFedwireAmount', ''));
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (
                this.showField(ctx) &&
                validateCheckBoxes([
                  ctx.values.TPCollectRequestEFTEnabled ?? false,
                  ctx.values.TPCollectRequestInteracEnabled ?? false,
                  ctx.values.TPCollectRequestCreditCardEnabled ?? false,
                  ctx.values.TPCollectRequestACHEnabled ?? false,
                  ctx.values.TPCollectRequestACHSameDayEnabled ?? false,
                  ctx.values.TPCollectRequestFedwireEnabled ?? false,
                ])
              ) {
                return Yup.string().required('Please select the field.');
              }
              return Yup.string().notRequired();
            },
          },
          TPCollectRequestFedwireAmount: {
            label: 'Total amount collected monthly with Fedwire',
            placeholder: 'Total amount collected monthly',
            fieldName: 'TPCollectRequestFedwireAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ) &&
              ctx.values.TPCollectRequestFedwireEnabled
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              (isInsideSales &&
                [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                  ctx.values.accountIntendedTransactionType ?? '',
                ) &&
                ctx.values.TPCollectRequestFedwireEnabled) ||
              false,
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx, undefined, (event) => {
                if (event && event.target) {
                  ctx.handleFieldChange(
                    vp.createSyntheticEvent(
                      'TPCollectRequestTotalAmount',
                      calculateTotal([
                        event.target.value,
                        ctx.values.TPCollectRequestInteracAmount ?? '',
                        ctx.values.TPCollectRequestCreditCardAmount ?? '',
                        ctx.values.TPCollectRequestEFTAmount ?? '',
                        ctx.values.TPCollectRequestACHAmount ?? '',
                        ctx.values.TPCollectRequestACHSameDayAmount ?? '',
                      ]),
                    ),
                  );
                }
              });
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPCollectRequestTotalAmount: {
            label: 'Total',
            placeholder: 'Total amount',
            fieldName: 'TPCollectRequestTotalAmount',
            initValue: '',
            dataType: 'number',
            getInputState: () => 'disabled',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPCollectRequestAvgRequestAmount: {
            label: 'The average collected amount is',
            placeholder: 'Average amount withdrawn monthly',
            fieldName: 'TPCollectRequestAvgRequestAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
          TPCollectRequestLargestRequestAmount: {
            label: 'The largest collected amount is',
            placeholder: 'The largest withdrawn amount',
            fieldName: 'TPCollectRequestLargestRequestAmount',
            initValue: '',
            dataType: 'number',
            getInputState: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? '')
                ? 'required'
                : 'optional',
            showField: (ctx: Ctx) =>
              isInsideSales &&
              [onlyCollectingMoney, sendingAndCollectingMoney].includes(
                ctx.values.accountIntendedTransactionType ?? '',
              ),
            getElement(ctx: Ctx) {
              return createNumberFieldLead(this, ctx);
            },
            validate(ctx: Ctx) {
              if (this.showField(ctx)) {
                return Yup.number().required('Please enter the amount.');
              }
              return Yup.number().notRequired();
            },
          },
        },
        showSection: (ctx: Ctx) =>
          isInsideSales &&
          [onlyCollectingMoney, sendingAndCollectingMoney].includes(ctx.values.accountIntendedTransactionType ?? ''),
      },
    ],
  };

  return useMemo(() => fieldUtilsFactory<NewAccountData>(sections), [dealCountry, accountTransactionTypes, country]);
};

function calculateTotal(values: string[]) {
  let total = 0;
  values.forEach((value) => {
    total += value === '' ? 0 : parseFloat(value);
  });
  return total.toString();
}

function validateCheckBoxes(values: boolean[]) {
  let validate = true;
  values.forEach((value) => {
    validate = validate && !value;
  });
  return validate;
}

export default useAccountMatrixCreationFields;
